import { Dtos, formatToDollar } from "@inrev/common";
import {
	createSummaryTo,
	SummaryCardSection,
	SummaryField,
	SummaryFieldGroup,
	SummarySection,
	type SummaryToFn,
} from "@portal/components/multiform";
import { DateTime } from "luxon";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { match } from "ts-pattern";
import { z } from "zod";
import { boolToYN } from "../../../../../../utils";
import { formatSummaryDateFromEmpty } from "../../../../../../utils/string";
import { type AccountFormValues, fp } from "../../schema";

const AccountRequest = Dtos.SuretyAccount.Create.Request.aiAccountSchema;
type AccountRequest = z.infer<typeof AccountRequest>;

type LargeAccount = Extract<AccountRequest, { modelSize: "large" }>;
type MediumAccount = Extract<AccountRequest, { modelSize: "medium" }>;
type MediumOrLargeAccount = MediumAccount | LargeAccount;

const ProfitabilitySection = ({
	profitability,
	effectiveFyeYear,
	showLastFye = true,
	pathname,
	to,
}: {
	profitability: {
		profitableYtd?: boolean | null;
		profitableLastFye?: boolean | null;
	};
	effectiveFyeYear: number;
	showLastFye?: boolean;
	pathname: string;
	to: SummaryToFn;
}) => (
	<SummarySection
		header="Profitability"
		to={{ pathname, section: "financials", id: "profitability" }}
	>
		<SummaryFieldGroup>
			<SummaryField
				label={`Profitable YTD (${effectiveFyeYear + 1}):`}
				value={boolToYN(profitability?.profitableYtd)}
				fieldPath={fp("account.financials.profitability.profitableYtd")}
				to={to("profitability")}
			/>
			{showLastFye && (
				<SummaryField
					label={`Profitable Last Year (FY${effectiveFyeYear}):`}
					value={boolToYN(profitability?.profitableLastFye)}
					fieldPath={fp("account.financials.profitability.profitableLastFye")}
					to={to("profitability")}
				/>
			)}
		</SummaryFieldGroup>
	</SummarySection>
);

const AiFinancialDocumentsSummary = ({
	files,
	pathname,
	partial,
}: {
	files: { id: string; name: string; extension: string }[];
	pathname: string;
	partial?: boolean;
}) => (
	<SummarySection
		header="Financial Documents"
		to={{ pathname, section: "financials", id: "financialDocuments" }}
	>
		<SummaryFieldGroup>
			<SummaryField
				label="Files Uploaded:"
				value={files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""}` : undefined}
				partial={partial && files.length === 0}
				to={{ pathname, section: "financials", id: "financialDocuments" }}
			/>
		</SummaryFieldGroup>
	</SummarySection>
);

const MediumOrLargeFinancials = ({
	account,
	effectiveFyeYear,
	pathname,
	to,
}: {
	account: MediumOrLargeAccount;
	effectiveFyeYear: number;
	pathname: string;
	to: SummaryToFn;
}) => {
	const isLarge = account.modelSize === "large";
	const { financials } = account;

	// Use match to get fully narrowed large financials for large-only field access
	const largeFinancials = match(account)
		.with({ modelSize: "large" }, (a) => a.financials)
		.otherwise(() => undefined);

	return (
		<SummaryCardSection header="Financials" to={{ pathname, section: "financials" }}>
			<AiFinancialDocumentsSummary files={financials.files ?? []} pathname={pathname} partial />
			<ProfitabilitySection
				profitability={financials.profitability ?? {}}
				effectiveFyeYear={effectiveFyeYear}
				showLastFye={false}
				pathname={pathname}
				to={to}
			/>
			<SummarySection header="Work In Progress" to={{ pathname, section: "financials", id: "wip" }}>
				<SummaryFieldGroup>
					<SummaryField
						label="Date:"
						value={formatSummaryDateFromEmpty(
							financials.workInProgressStatement?.date as string | undefined,
						)}
						fieldPath={fp("account.financials.workInProgressStatement.date")}
						to={to("wip")}
					/>
					<SummaryField
						label="Backlog Cost-to-Complete:"
						value={formatToDollar(financials.workInProgressStatement?.projectBacklogCost)}
						fieldPath={fp("account.financials.workInProgressStatement.projectBacklogCost")}
						to={to("wip")}
					/>
					{isLarge && (
						<SummaryField
							label="Gross Profit in the Backlog:"
							value={formatToDollar(
								largeFinancials?.workInProgressStatement?.projectBacklogGrossProfit,
							)}
							fieldPath={fp("account.financials.workInProgressStatement.projectBacklogGrossProfit")}
							to={to("wip")}
						/>
					)}
				</SummaryFieldGroup>
			</SummarySection>
			{isLarge && (
				<SummarySection
					header="Bank Details"
					to={{ pathname, section: "financials", id: "bankDetails" }}
				>
					<SummaryFieldGroup>
						<SummaryField
							label="Revolving Line of Credit Limit:"
							value={formatToDollar(largeFinancials?.blocSize)}
							fieldPath={fp("account.financials.blocSize")}
							to={to("bankDetails")}
						/>
					</SummaryFieldGroup>
				</SummarySection>
			)}
		</SummaryCardSection>
	);
};

export const FinancialsSummary = () => {
	const { getValues } = useFormContext<AccountFormValues>();
	const { pathname } = useLocation();
	const account = getValues().account;
	const to = createSummaryTo(pathname, "financials");
	const effectiveFyeYear = DateTime.now().year - 1;

	return match(account)
		.with({ modelSize: "medium" }, { modelSize: "large" }, (a) => (
			<MediumOrLargeFinancials
				account={a}
				effectiveFyeYear={effectiveFyeYear}
				pathname={pathname}
				to={to}
			/>
		))
		.otherwise(() => (
			<SummaryCardSection header="Financials" to={{ pathname, section: "financials" }}>
				{account.financials?.files && (
					<AiFinancialDocumentsSummary files={account.financials.files} pathname={pathname} />
				)}
				<ProfitabilitySection
					profitability={account.financials?.profitability ?? {}}
					effectiveFyeYear={effectiveFyeYear}
					pathname={pathname}
					to={to}
				/>
			</SummaryCardSection>
		));
};
